import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, type User } from '../services/api'
import { formatDistanceToNow } from 'date-fns'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import PageHeader from '../nexus/components/PageHeader'
import TableCard, { TableSearch } from '../nexus/components/TableCard'
import { useAnalyticsPeriod } from '../lib/analytics-period'
import ModelUsageBreakdown, { formatUsd } from '../components/ModelUsageBreakdown'

type SortField = 'name' | 'email' | 'created_at' | 'last_active' | 'session_count' | 'lifetime_response_count' | 'lifetime_token_count' | 'org_name'
type SortDirection = 'asc' | 'desc'

const statusTone = {
  'Free Trial': 'badge-warning',
  Subscribed: 'badge-success',
  Staff: 'badge-secondary',
  Helpbot: 'badge-info',
} as const

export default function Users() {
  const navigate = useNavigate()
  const { period } = useAnalyticsPeriod()
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isExporting, setIsExporting] = useState(false)

  // Debounced so typing does not fire a request per keystroke.
  const [submittedSearch, setSubmittedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setSubmittedSearch(search.trim())
      setCurrentPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search, period])

  // Server-side paging and search. Both have to be server-side together: the
  // list was capped at 100 rows, so client-side search could only ever match
  // within that slice and silently missed everyone outside it.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['users', period, submittedSearch, currentPage, pageSize, sortField, sortDirection],
    queryFn: () =>
      api.getUsers({
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        search: submittedSearch || undefined,
        sort_by: sortField,
        sort_direction: sortDirection,
        period,
      }),
    staleTime: 30000,
    placeholderData: keepPreviousData,
  })

  const users = data?.users
  const totalUsers = data?.total ?? 0

  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize))
  const paginatedUsers = users ?? []

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const handleSort = (field: SortField) => {
    setCurrentPage(1)
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const exportUsers = await api.getAllUsers({
        search: submittedSearch || undefined,
        sort_by: sortField,
        sort_direction: sortDirection,
        period,
      })
      const csvCell = (value: unknown) => {
        let text = value == null ? '' : String(value)
        // Prevent spreadsheet formula execution when an exported value begins
        // with a formula sigil.
        if (/^[=+\-@]/.test(text)) text = `'${text}`
        return `"${text.replace(/"/g, '""')}"`
      }
      const rows = [
        ['Name', 'Email', 'Status', 'Created', 'Last active', 'Sessions', 'Messages', 'Responses', 'Tokens', 'Spend (USD)', 'Model breakdown', 'Organization'],
        ...exportUsers.map((user) => [
          user.name,
          user.email,
          user.account_status,
          user.created_at,
          user.last_active,
          user.session_count ?? 0,
          user.message_count ?? 0,
          user.lifetime_response_count ?? 0,
          user.privacy_restricted ? 'Restricted' : user.lifetime_token_count ?? 0,
          user.privacy_restricted ? 'Restricted' : user.has_unpriced_models ? `${formatUsd(user.spend_usd)} + unpriced models` : formatUsd(user.spend_usd),
          user.privacy_restricted ? 'Restricted' : (user.model_usage || []).map((item) => `${item.model}: ${item.total_tokens} tokens / ${formatUsd(item.spend_usd)}`).join('; '),
          user.org_name,
        ]),
      ]
      const csv = `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\r\n')}`
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `mate-users-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      window.alert(error instanceof Error ? `Could not export users: ${error.message}` : 'Could not export users.')
    } finally {
      setIsExporting(false)
    }
  }

  const Th = ({ field, label }: { field: SortField; label: string }) => (
    <th onClick={() => handleSort(field)} className="cursor-pointer select-none whitespace-nowrap">
      <span className="inline-flex items-center gap-1">
        {label}
        <span
          className={`iconify size-3.5 ${
            sortField !== field
              ? 'lucide--chevrons-up-down opacity-30'
              : sortDirection === 'asc'
                ? 'lucide--chevron-up'
                : 'lucide--chevron-down'
          }`}
        />
      </span>
    </th>
  )

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="Users" />
        <div className="card bg-base-100">
          <div className="card-body items-center text-center gap-3 py-12">
            <span className="iconify lucide--triangle-alert text-error size-10" />
            <h2 className="card-title">Failed to load users</h2>
            <p className="text-base-content/70">{error instanceof Error ? error.message : 'Unknown error'}</p>
            <button className="btn btn-primary btn-sm mt-2" onClick={() => refetch()}>Try again</button>
          </div>
        </div>
      </div>
    )
  }

  const start = (currentPage - 1) * pageSize

  return (
    <div className="p-6">
      <PageHeader
        title="Users"
        subtitle="Everyone who has interacted with Mat-E."
        actions={
          <div className="flex items-center gap-2">
            <span className="badge badge-soft badge-primary">
              {totalUsers.toLocaleString()} users
            </span>
            <button className="btn btn-sm btn-primary" onClick={handleExport} disabled={isExporting || isLoading}>
              <span className={`iconify ${isExporting ? 'lucide--loader-circle animate-spin' : 'lucide--download'} size-4`} />
              {isExporting ? 'Exporting…' : 'Export users'}
            </button>
          </div>
        }
      />

      <TableCard
        toolbar={
          <>
            <div className="inline-flex items-center gap-3">
              <TableSearch value={search} onChange={handleSearch} placeholder="Search name, email, or ID" />
              <select
                className="select select-sm w-28 hidden sm:block"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
                aria-label="Per page"
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={200}>200 / page</option>
              </select>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => refetch()}>
              <span className="iconify lucide--refresh-cw size-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </>
        }
        footer={
          <>
            <span className="text-base-content/60 text-sm">
              {paginatedUsers.length === 0
                ? 'No users'
                : `Showing ${start + 1}–${Math.min(start + paginatedUsers.length, totalUsers)} of ${totalUsers.toLocaleString()}`}
            </span>
            {totalPages > 1 && (
              <div className="join">
                <button className="join-item btn btn-sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  «
                </button>
                <span className="join-item btn btn-sm btn-ghost pointer-events-none">
                  Page {currentPage} / {totalPages}
                </span>
                <button className="join-item btn btn-sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  »
                </button>
              </div>
            )}
          </>
        }
      >
        <thead>
          <tr>
            <Th field="name" label="Name" />
            <Th field="email" label="Email" />
            <th className="whitespace-nowrap">Status</th>
            <Th field="created_at" label="Created" />
            <Th field="last_active" label="Last active" />
            <Th field="session_count" label="Sessions" />
            <th className="whitespace-nowrap">Messages</th>
            <Th field="lifetime_response_count" label="Responses" />
            <Th field="lifetime_token_count" label="Tokens" />
            <th className="whitespace-nowrap">Spend</th>
            <Th field="org_name" label="Org" />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={11} className="text-center py-12">
                <span className="loading loading-spinner loading-md" />
              </td>
            </tr>
          ) : paginatedUsers.length === 0 ? (
            <tr>
              <td colSpan={11} className="text-center py-12 text-base-content/60">
                {search ? 'No users match your search' : 'No users found'}
              </td>
            </tr>
          ) : (
            paginatedUsers.map((user: User) => (
              <tr key={user.user_id} className="hover:bg-base-200/40 *:text-nowrap cursor-pointer" onClick={() => navigate(`/users/${user.user_id}`)} tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') navigate(`/users/${user.user_id}`) }} aria-label={`Open profile for ${user.name || user.email || user.user_id}`}>
                <td className="font-medium">{user.name || 'Unknown'}</td>
                <td className="text-sm text-base-content/70">{user.email || '—'}</td>
                <td>{user.account_status && <span className={`badge badge-soft badge-sm ${statusTone[user.account_status]}`}>{user.account_status}</span>}</td>
                <td className="text-sm">{formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}</td>
                <td className="text-sm">
                  {user.last_active ? formatDistanceToNow(new Date(user.last_active), { addSuffix: true }) : 'Never'}
                </td>
                <td><span className="badge badge-soft badge-sm">{user.session_count || 0}</span></td>
                <td><span className="badge badge-soft badge-sm">{user.message_count || 0}</span></td>
                <td className="text-sm tabular-nums">{(user.lifetime_response_count ?? 0).toLocaleString()}</td>
                <td className="text-sm tabular-nums">
                  {user.privacy_restricted ? <span className="text-base-content/50">Restricted</span> : <><div>{(user.lifetime_token_count ?? 0).toLocaleString()}</div><ModelUsageBreakdown usage={user.model_usage || []} /></>}
                </td>
                <td className="text-sm tabular-nums">
                  {user.privacy_restricted ? <span className="text-base-content/50">Restricted</span> : <><div>{formatUsd(user.spend_usd)}</div>{user.has_unpriced_models && <div className="text-xs text-warning">plus unpriced usage</div>}</>}
                </td>
                <td>
                  {user.org_name ? (
                    <span className="badge badge-outline badge-sm">{user.org_name}</span>
                  ) : (
                    <span className="text-base-content/30 text-xs">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </TableCard>
    </div>
  )
}
